import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../routes/routeConfig';
import BrandMark from '../../components/BrandMark';
import SiteFooter from '../../components/SiteFooter';
import CommunityBanner from '../../components/CommunityBanner';
import BookCarousel from './books/BookCarousel';
import { ACADEMY_CATEGORIES, fallbackAcademyDocs, fallbackBooks, fetchAcademyBooks, fetchAcademyDocs } from './academyData';
import { getBookCover } from '../books/bookCovers';
import './academy.css';

const wisdomQuotes = [
  {
    name: '沃伦·巴菲特',
    title: '伯克希尔·哈撒韦 CEO',
    quote: '别人贪婪时我恐惧，别人恐惧时我贪婪。',
  },
  {
    name: '查理·芒格',
    title: '长期主义投资者',
    quote: '所谓常识，是平常人没有的常识。',
  },
  {
    name: '彼得·林奇',
    title: '麦哲伦基金前经理',
    quote: '知道你持有什么，以及为什么持有它。',
  },
];

const pathSteps = [
  {
    number: '01',
    tag: '免费',
    tone: 'free',
    title: '认知入门',
    desc: '阅读基础文章，了解财富思维和加密货币核心概念。',
    path: ROUTES.academy,
  },
  {
    number: '02',
    tag: '书单',
    tone: 'book',
    title: '系统阅读',
    desc: '精选投资经典书籍，构建完整的知识体系。',
    path: ROUTES.academy,
  },
  {
    number: '03',
    tag: '观点',
    tone: 'opinion',
    title: '观点校准',
    desc: '聆听投资大师智慧，建立正确的交易思维。',
    path: ROUTES.academy,
  },
  {
    number: '04',
    tag: '进阶',
    tone: 'advanced',
    title: '准备实战',
    desc: '连接市场研究院和工具箱，把学习转成复盘动作。',
    path: ROUTES.market,
  },
];

const stripMarkdown = (content = '') => (
  content
    .replace(/!\[[^\]]*]\([^)]*\)/g, '')
    .replace(/\[[^\]]*]\([^)]*\)/g, '')
    .replace(/[#>*_`|\-:]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
);

const getAuthor = (content = '') => {
  const match = content.match(/作者[：:]\s*([^)\n]+(?:\([^)]*\))?)/);
  return match?.[1]?.replace(/\*\*/g, '').trim() || 'GHOST 书单';
};

const docsByCategory = (items, category) => items.filter((item) => item.category === category);

export default function AcademyPage() {
  const navigate = useNavigate();
  const [books, setBooks] = useState(fallbackBooks);
  const [academyDocs, setAcademyDocs] = useState(fallbackAcademyDocs);

  useEffect(() => {
    let cancelled = false;

    fetchAcademyBooks().then((nextBooks) => {
      if (!cancelled) setBooks(nextBooks);
    });

    fetchAcademyDocs().then((nextDocs) => {
      if (!cancelled) setAcademyDocs(nextDocs);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const allArticles = docsByCategory(academyDocs, ACADEMY_CATEGORIES.articles);
  const allGuides = docsByCategory(academyDocs, ACADEMY_CATEGORIES.guides);
  const articles = allArticles.slice(0, 3);
  const guides = allGuides.slice(0, 4);
  const totalBooks = books.length;
  const totalArticles = allArticles.length;
  const totalGuides = allGuides.length;
  const totalItems = totalBooks + academyDocs.length;

  const goDoc = (id) => navigate(`/docs/${id}`);

  const carouselBooks = books.slice(0, 3).map((book) => ({
    id: book.id,
    title: book.title,
    author: book.author || getAuthor(book.content),
    cover: book.cover || getBookCover(book.id),
    desc: stripMarkdown(book.summary || book.content).slice(0, 90),
    onClick: () => navigate(`/book/${book.id}`),
  }));

  return (
    <main className="academy-page academy-magazine">
      <section className="academy-mag-hero">
        <img className="academy-hero-bg" src="/images/brand/polaris-bitcoin-bg.png" alt="" />
        <div className="academy-hero-inner">
          <div className="academy-hero-top">
            <div>
              <div className="academy-eyebrow">GHOST · 财富学院</div>
              <h1 className="academy-title academy-brand-lockup">
                <span className="academy-brand-text">GHOST</span>
                <BrandMark className="academy-brand-mark" alt="GHOST" />
                <span className="academy-brand-subtitle">面向加密市场的学习工作台</span>
              </h1>
            </div>
            <div className="academy-hero-note">
              <p>从财富认知、交易书籍到交易所教程，把零散学习整理成可执行的成长路径。</p>
              <button type="button" onClick={() => navigate(ROUTES.academy)}>开始学习</button>
            </div>
          </div>

          <div className="academy-hero-bottom">
            <div>
              <p className="academy-mini-label">当前内容库</p>
              <div className="academy-brand-strip">
                <span>财富认知 {totalArticles}</span>
                <span>推荐书籍 {totalBooks}</span>
                <span>交易教程 {totalGuides}</span>
              </div>
            </div>
            <div className="academy-stats-row">
              <div><strong>{totalItems}</strong><span>内容条目</span></div>
              <div><strong>4</strong><span>学习阶段</span></div>
              <div><strong>3</strong><span>核心栏目</span></div>
            </div>
          </div>
        </div>
      </section>

      <div className="academy-wrap">
        <section className="academy-sec">
          <div className="academy-sec-label">Learning Path</div>
          <h2 className="academy-sec-title">学习路径</h2>
          <p className="academy-sec-desc">循序渐进的四步学习体系，从入门认知到实战进阶。</p>
          <div className="academy-path-cols">
            {pathSteps.map((step, index) => (
              <div className="academy-path-fragment" key={step.number}>
                <button className="academy-path-step" type="button" onClick={() => navigate(step.path)}>
                  <span className="academy-path-num">{step.number}</span>
                  <span className={`academy-path-tag academy-path-tag-${step.tone}`}>{step.tag}</span>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="academy-sec">
          <div className="academy-sec-label">Bookshelf</div>
          <h2 className="academy-sec-title">推荐书籍</h2>
          <p className="academy-sec-desc">精选投资经典，每一本都连接到对应详情页。</p>
          <BookCarousel books={carouselBooks} />
          {totalBooks > carouselBooks.length && (
            <div className="academy-sec-foot">
              <button type="button" onClick={() => navigate(ROUTES.academyBooks)}>查看全部 {totalBooks} 本 →</button>
            </div>
          )}
        </section>

        <section className="academy-sec">
          <div className="academy-sec-label">Wisdom</div>
          <h2 className="academy-sec-title">名人观点</h2>
          <p className="academy-sec-desc">用少量高质量观点校准风险、纪律和长期主义。</p>
          <div className="academy-quotes-grid">
            {wisdomQuotes.map((item) => (
              <article className="academy-wisdom-card" key={item.name}>
                <blockquote>“{item.quote}”</blockquote>
                <div>
                  <strong>{item.name}</strong>
                  <span>{item.title}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="academy-sec">
          <div className="academy-sec-label">Wealth Cognition</div>
          <h2 className="academy-sec-title">精选文章</h2>
          <p className="academy-sec-desc">深入理解财富本质，建立正确的投资认知框架。</p>
          <div className="academy-article-grid">
            {articles.map((article) => (
              <button className="academy-article-card" type="button" key={article.id} onClick={() => goDoc(article.id)}>
                <span>{article.category}</span>
                <h3>{article.title}</h3>
                <p>{stripMarkdown(article.content).slice(0, 110)}...</p>
              </button>
            ))}
          </div>
          {totalArticles > articles.length && (
            <div className="academy-sec-foot">
              <button type="button" onClick={() => navigate(ROUTES.academyArticles)}>查看全部 {totalArticles} 篇 →</button>
            </div>
          )}
        </section>

        <section className="academy-sec academy-sec-last">
          <div className="academy-sec-label">Exchange Guide</div>
          <h2 className="academy-sec-title">交易所教程</h2>
          <p className="academy-sec-desc">手把手完成从下载、认证到实盘交易的每一步。</p>
          <div className="academy-guide-list">
            {guides.map((guide, index) => (
              <button className="academy-guide-row" type="button" key={guide.id} onClick={() => goDoc(guide.id)}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{guide.title}</strong>
                <em>查看教程</em>
              </button>
            ))}
          </div>
          {totalGuides > guides.length && (
            <div className="academy-sec-foot">
              <button type="button" onClick={() => navigate(`${ROUTES.academyArticles}?category=${encodeURIComponent(ACADEMY_CATEGORIES.guides)}`)}>查看全部 {totalGuides} 篇 →</button>
            </div>
          )}
        </section>

        <section className="academy-cta">
          <div>
            <span>Next</span>
            <h2>学完认知，再进入市场研究院。</h2>
          </div>
          <button type="button" onClick={() => navigate(ROUTES.market)}>进入市场研究院</button>
        </section>

        <section className="academy-sec">
          <CommunityBanner />
        </section>
      </div>

      <SiteFooter />
    </main>
  );
}
